import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import ProductItem from "../ProductItem/ProductItem";
import "./productGrid.css";
import sourRaspberry from "../../assets/images/vape/sourRaspberry.webp";

export const productColors = [
  "#CA6279", "#35714C", "#C51623", "#854495", "#3F55A3", "#E3CE77",
  "#C7337A", "#716494", "#D2813B", "#831B27", "#A1C987", "#8393B8",
  "#DC6B4D", "#DB4A42", "#CD52A2", "#89BB6B", "#BD0B4D", "#68A79B",
  "#E5B749", "#C42F51", "#C86178",
];

// Фильтры каталога: swatch — цвет кружка рядом с названием
const FILTERS = [
  { key: "all", label: "Все" },
  { key: "new", label: "Новинки" },
  { key: "popular", label: "Популярные", optional: true },
  { key: "code46", label: "CODE 46", line: true },
  { key: "code23", label: "CODE 23", line: true },
  { key: "code55", label: "CODE 30/55", line: true },
  { key: "blue", label: "Синие", swatch: "#2233DD" },
  { key: "mint", label: "Мятные", swatch: "#4BE3B8" },
  { key: "pink", label: "Розовые", swatch: "#F2224F" },
  { key: "classic", label: "Классика", swatch: "#3A3550" },
];

const LINE_KEYS = new Set(FILTERS.filter((f) => f.line).map((f) => f.key));

const matches = (product, key, popularIds) => {
  if (key === "all") return true;
  if (key === "new") return Boolean(product.isNew);
  if (key === "popular") return popularIds.has(product.id);
  if (LINE_KEYS.has(key)) return product.line === key;
  return product.color === key;
};

// Случайная выборка «популярных» — фиксируется один раз на загрузку страницы,
// чтобы список не перетасовывался при каждом рендере
const shuffled = (items) => {
  const pool = [...items];
  for (let i = pool.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool;
};

const pickRandom = (items, count) =>
  new Set(shuffled(items).slice(0, Math.min(count, items.length)).map((p) => p.id));

const ProductGrid = memo(({
  products: productsProp,
  showFilters = false,
  showPopular = false,
  popularCount = 8,
  pageSize = 0,
  shuffle = false,
  filterKeys = null,
}) => {
  const storeProducts = useSelector((state) => state.shop.products);
  const sourceProducts = productsProp ?? storeProducts;

  // Порядок перемешиваем один раз на загрузку страницы
  const allProducts = useMemo(
    () => (shuffle ? shuffled(sourceProducts) : sourceProducts),
    [sourceProducts, shuffle]
  );

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const path = pathname.replace(/\/$/, "");
  const hide = path.startsWith("/code46") || path.startsWith("/code23");

  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const filtersRef = useRef(null);
  const listRef = useRef(null);
  const isFirstRender = useRef(true);

  // Фон карточки закрепляем за товаром, чтобы он не «прыгал» при фильтрации.
  // Если у товара задан свой цвет под вкус (product.bg) — берём его,
  // иначе падаем на палитру по индексу.
  const bgById = useMemo(() => {
    const map = new Map();
    sourceProducts.forEach((p, i) => map.set(p.id, p.bg ?? productColors[i % productColors.length]));
    return map;
  }, [sourceProducts]);

  const popularIds = useMemo(
    () => (showPopular ? pickRandom(allProducts, popularCount) : new Set()),
    [allProducts, popularCount, showPopular]
  );

  const hasBothLines = useMemo(
    () => new Set(allProducts.map((p) => p.line).filter(Boolean)).size > 1,
    [allProducts]
  );

  // filterKeys — явный список кнопок для страницы (порядок берём из FILTERS).
  // Без него набор собирается автоматически: цвета + новинки, линейки — только
  // когда в выборке больше одной линейки.
  const visibleFilters = useMemo(
    () =>
      FILTERS.filter((f) => {
        if (filterKeys) return filterKeys.includes(f.key);
        if (f.key === "popular") return showPopular;
        if (f.line) return hasBothLines;
        return !f.optional;
      }),
    [showPopular, hasBothLines, filterKeys]
  );

  const counts = useMemo(() => {
    const result = {};
    visibleFilters.forEach((f) => {
      result[f.key] = allProducts.filter((p) => matches(p, f.key, popularIds)).length;
    });
    return result;
  }, [allProducts, visibleFilters, popularIds]);

  const filtered = useMemo(
    () => (showFilters ? allProducts.filter((p) => matches(p, filter, popularIds)) : allProducts),
    [allProducts, filter, showFilters, popularIds]
  );

  const paginated = pageSize > 0;
  const totalPages = paginated ? Math.max(1, Math.ceil(filtered.length / pageSize)) : 1;
  const currentPage = Math.min(page, totalPages);

  const visible = useMemo(() => {
    if (!paginated) return filtered;
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize, paginated]);

  // Смена фильтра всегда возвращает на первую страницу
  useEffect(() => {
    setPage(1);
  }, [filter]);

  // При листании подматываем к строке кнопок, а не к началу всего блока.
  // Шапка у нас position: fixed, поэтому вычитаем её реальную высоту,
  // иначе кнопки уезжают под навбар.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const target = filtersRef.current ?? listRef.current;
    if (!target) return;

    const header = document.querySelector(".header");
    const offset = (header?.offsetHeight ?? 0) + 16;
    const top = target.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
  }, [currentPage, filter]);

  return (
    <div
      className="product-grid-container fade-in"
      style={{ animationDelay: "0.3s" }}
    >
      {/* --- Инфоблок перед списком --- */}
      <div className="product-grid-intro" aria-labelledby="products-heading">
        <div className="product-grid-intro__text">
          <h1 id="products-heading" className="product-grid-intro__title">
            Подбери свой CODE вкуса
          </h1>
          <p className="product-grid-intro__desc">
            Мы взяли классические и всеми любимые вкусы и поместили их в наш вейп,
            сохранив натуральное послевкусие
          </p>
        </div>

       {!hide && ( <button
          onClick={() => navigate("/code46")}
          className="product-grid-intro__cta"
          aria-label="Перейти в каталог Code46"
        >
          <img
            src={sourRaspberry}
            alt="catalog"
            className="product-grid-intro__cta-icon"
          />
          <span className="catalog-name">В КАТАЛОГ</span>
        </button>
       )}
      </div>

      {/* --- Фильтры по новинкам и цветам --- */}
      {showFilters && (
        <div ref={filtersRef} className="product-filters" role="tablist" aria-label="Фильтр каталога">
          {visibleFilters.filter((f) => counts[f.key] > 0).map((f) => (
            <button
              key={f.key}
              type="button"
              role="tab"
              aria-selected={filter === f.key}
              onClick={() => setFilter(f.key)}
              className={`product-filter${filter === f.key ? " is-active" : ""}`}
            >
              {f.swatch && (
                <span
                  className="product-filter__dot"
                  style={{ backgroundColor: f.swatch }}
                  aria-hidden
                />
              )}
              <span className="product-filter__label">{f.label}</span>
              <span className="product-filter__count">{counts[f.key]}</span>
            </button>
          ))}
        </div>
      )}

      <div ref={listRef}>
      <div key={paginated ? `${filter}-${currentPage}` : undefined} className={paginated ? "product-list-swap" : undefined}>
      {/* --- Лента для мобильных --- */}
      <div className="mobile-scroll">
        {visible.map((product, index) => (
          <div key={product.id ?? index} className={`scroll-item${paginated ? "" : " fade-in"}`}>
            <ProductItem
              product={product}
              bgColor={bgById.get(product.id) ?? productColors[index % productColors.length]}
              index={index}
            />
          </div>
        ))}
      </div>

      {/* --- Сетка для десктопов --- */}
      <div className="desktop-grid">
        {visible.map((product, index) => (
          <div
            key={product.id ?? index}
            className={`grid-item${paginated ? "" : " fade-in"}`}
            style={paginated ? undefined : { animationDelay: `${0.1 * (index + 1)}s` }}
          >
            <ProductItem
              product={product}
              bgColor={bgById.get(product.id) ?? productColors[index % productColors.length]}
              index={index}
            />
          </div>
        ))}
      </div>
      </div>
      </div>

      {/* --- Пагинация --- */}
      {paginated && totalPages > 1 && (
        <nav className="product-pagination" aria-label="Страницы каталога">
          <button
            type="button"
            className="product-pagination__arrow"
            onClick={() => setPage(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Предыдущая страница"
          >
            ‹
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              type="button"
              className={`product-pagination__page${n === currentPage ? " is-active" : ""}`}
              onClick={() => setPage(n)}
              aria-current={n === currentPage ? "page" : undefined}
            >
              {n}
            </button>
          ))}

          <button
            type="button"
            className="product-pagination__arrow"
            onClick={() => setPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label="Следующая страница"
          >
            ›
          </button>
        </nav>
      )}
    </div>
  );
});

export default ProductGrid;
